import { FC, useLayoutEffect, useState } from 'react';
import {
  themeParams,
  swipeBehavior,
  viewport,
  closingBehavior,
  postEvent,
  retrieveLaunchParams,
  isTMA,
} from '@tma.js/sdk-react';
import { initializeTelegramSDK } from 'shared/helpers/initializeTelegramSDK/initializeTelegramSDK';
import { initializeMaxSDK } from 'shared/helpers/max/initializeMaxSDK';
import { LoaderTriangles } from 'shared/components/loader/LoderLogo';
import { ErrorWrapper } from 'shared/components/ErrorWrapper';
import { AuthBootstrap } from './providers/authBootstrap';
import withProviders from './providers';
import Routing from '../pages';
import { getDeviceInfo } from 'shared/utils/utils';
import CONFIG from 'shared/constants/config';

// Инициализация параметров Telegram (вызывается только для TG)
const initTelegramParams = async () => {
  try {
    if (!themeParams.isMounted()) {
      themeParams.mount();
      themeParams.bindCssVars();
      closingBehavior.mount();
      closingBehavior.disableConfirmation();

      if (swipeBehavior.isSupported()) {
        swipeBehavior.mount();
        swipeBehavior.disableVertical();
      }

      await viewport.mount();
      viewport.expand();
      viewport.bindCssVars();

      // Запрет поворота
      postEvent('web_app_toggle_orientation_lock', { locked: true });

      // Проверка платформы через retrieveLaunchParams (уже безопасно, т.к. инициализировано)
      const lp = retrieveLaunchParams();
      const isMobile = getDeviceInfo(lp.tgWebAppPlatform).isMobile;
      if (isMobile) {
        await viewport.requestFullscreen();
      }
    }
  } catch (e) {
    console.error('Failed to init Telegram params:', e);
  }
};

const App: FC = () => {
  useLayoutEffect(() => {
    window.addEventListener('error', e => {
      if (
        /Loading chunk [\d]+ failed/.test(e.message) ||
        /Unexpected token '<'/.test(e.message)
      ) {
        window.location.reload();
      }
    });
  }, []);

  return <Routing />;
};

const WrapProviders: FC = () => {
  const [init, setInit] = useState(false);
  const [error, setError] = useState('');

  useLayoutEffect(() => {
    let mounted = true;
    const sp = new URLSearchParams(window.location.search);

    // 1. Сначала определяем окружение максимально просто
    const wa = (window as any).WebApp;
    const maxRaw =
      wa?.initData ||
      (typeof wa?.initDataManager?.rawInitData === 'string'
        ? wa.initDataManager.rawInitData
        : '');
    const isMAX =
      (!!wa && !!maxRaw && maxRaw !== '') || sp.get('max_mock') === 'true';

    const isTG = isTMA() || CONFIG.ENV_MOCK;

    (async () => {
      try {
        if (isMAX) {
          await initializeMaxSDK();
        } else if (isTG) {
          await initializeTelegramSDK();
          await initTelegramParams();
        }

        // Установка стилей для TG Mobile (делаем только в эффекте)
        if (isTG) {
          try {
            const lp = retrieveLaunchParams();
            if (getDeviceInfo(lp.tgWebAppPlatform).isMobile) {
              document.body.classList.add('body--tma');
            }
          } catch (e) {}
        }

        if (mounted) setInit(true);
      } catch (e) {
        console.error('Initialization error:', e);
        if (mounted) setError('ERROR INIT');
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  if (error) return <ErrorWrapper message={error} />;
  if (!init) return <LoaderTriangles />;

  return (
    <AuthBootstrap>
      <App />
    </AuthBootstrap>
  );
};

export default withProviders(WrapProviders);
